/**
 * TechnologyDomainForm Component
 *
 * Reusable form for creating and editing technology domains
 */

import { TextField, type CreateTechnologyDomainFormData } from '../forms';
import { createTechnologyDomainSchema } from '../forms/validation';
import { EntityForm } from '../shared';
import { TagPicker } from '../tags/TagPicker';
import '@tylertech/forge/card';
import '@tylertech/forge/icon';
import './TechnologyDomainForm.css';

export interface TechnologyDomainFormProps {
  /** Initial form values (for edit mode) */
  defaultValues?: Partial<CreateTechnologyDomainFormData>;
  /** Submit handler */
  onSubmit: (data: CreateTechnologyDomainFormData) => void | Promise<void>;
  /** Cancel handler */
  onCancel: () => void;
  /** Is form submitting */
  isSubmitting?: boolean;
  /** Form mode */
  mode: 'create' | 'edit';
}

const CardElement = 'forge-card' as React.ElementType;

/**
 * TechnologyDomain create/edit form with validation
 *
 * @example
 * ```tsx
 * <TechnologyDomainForm
 *   mode="create"
 *   onSubmit={handleCreate}
 *   onCancel={() => navigate('/technology-domains')}
 * />
 * ```
 */
export function TechnologyDomainForm({
  defaultValues,
  onSubmit,
  onCancel,
  isSubmitting = false,
  mode,
}: TechnologyDomainFormProps): JSX.Element {
  return (
    <EntityForm<CreateTechnologyDomainFormData>
      schema={createTechnologyDomainSchema}
      baseValues={{
        tags: [],
      }}
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      onCancel={onCancel}
      isSubmitting={isSubmitting}
      mode={mode}
      createLabel="Create Technology Domain"
      formClassName="technology-domain-form"
    >
      {({ register, formState: { errors }, values, setValue }) => {
        const tags = values.tags || [];

        return (
          <>
            <CardElement className="form-card">
              <div className="form-section">
                <h2 className="forge-typography--headline6">Basic Information</h2>

                <TextField
                  label="Name"
                  {...register('name')}
                  value={values.name || ''}
                  error={errors.name}
                  required
                  placeholder="e.g., Security & Compliance"
                  helperText="A descriptive name for the technology domain"
                />

                <TextField
                  label="Description"
                  {...register('description')}
                  value={values.description || ''}
                  error={errors.description}
                  placeholder="Brief description of the technology domain"
                  helperText="Optional description (max 2000 characters)"
                />
              </div>
            </CardElement>

            <CardElement className="form-card">
              <div className="form-section">
                <h2 className="forge-typography--headline6">Additional Information</h2>

                <TagPicker
                  id="technology-domain-tag-picker"
                  label="Tags"
                  selectedTagIds={tags}
                  onChange={(newTagIds) => setValue('tags', newTagIds)}
                />

                <TextField
                  label="Notes"
                  {...register('notes')}
                  value={values.notes || ''}
                  error={errors.notes}
                  placeholder="Additional notes about this technology domain"
                  helperText="Optional notes (max 5000 characters)"
                />
              </div>
            </CardElement>
          </>
        );
      }}
    </EntityForm>
  );
}
